import React, { useMemo } from 'react';
import type { DailyData } from '../../types';
import { fmtMoneySmart } from '../../utils/formatters';
import { resolveTradeRealizedPnl, type AnyTrade } from '../../lib/trades/authoritativeTradeData';

interface TradePlStandardDeviationWidgetProps {
  dailyData?: DailyData[];
  trades?: Record<string, any>[];
  size?: 'mini' | 'regular' | 'full';
}

type LayoutProps = {
  stdDev: number;
  avgPnL: number;
  volRatio: number;
  status: string;
  statusColor: string;
  dotColor: string;
  insufficient?: boolean;
};

/*
Mini Layout:
- Compact horizontal card used in dashboard "mini" slots.
- Status badge positioned at the top-left (aligned with card padding).
- Large Standard Deviation value displayed prominently on the left.
- Two small metric blocks on the right: Avg P&L and Vol. Ratio.
*/
const TradePlStdDevMini: React.FC<LayoutProps> = ({ stdDev, avgPnL, volRatio, status, statusColor, dotColor, insufficient }) => (
  <div className="h-full w-full flex flex-col px-0 py-0 justify-between">
    <div className="flex flex-1 items-end justify-between gap-3 mb-0">
      {/* Left section (primary amount and status badge below it to avoid overlap) */}
      <div className="flex flex-col pl-0 pb-1">
        <span className={`block ml-0 text-4xl sm:text-3.5xl font-bold tracking-tight leading-none ${stdDev >= 0 && !insufficient ? 'text-emerald-600' : 'text-zinc-500'}`}>
          {insufficient ? 'N/A' : fmtMoneySmart(stdDev)}
        </span>
        <div className="flex items-center gap-2 mt-5">
          <div className={`w-2 h-2 rounded-full ${insufficient ? 'bg-zinc-500' : dotColor}`} />
          <span className={`text-[11px] font-semibold uppercase tracking-[0.25em] ${insufficient ? 'text-zinc-500' : statusColor} leading-none`}>
            {insufficient ? 'NEED 2+ TRADES' : status}
          </span>
        </div>
      </div>

      {/* Right section: two metric blocks */}
      <div className="flex items-end gap-5 pb-1">
        <div className="flex flex-col items-end text-right">
          <span className="uppercase tracking-wider text-zinc-500 text-xs font-medium">AVG P&L</span>
          <span className={`text-sm font-semibold mt-1 ${insufficient && avgPnL === 0 ? 'text-zinc-500' : avgPnL >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{insufficient && avgPnL === 0 ? 'N/A' : fmtMoneySmart(avgPnL)}</span>
        </div>
        <div className="flex flex-col items-center text-center">
          <span className="uppercase tracking-wider text-zinc-500 text-xs font-medium">VOL. RATIO</span>
          <span className={`text-sm font-semibold ${insufficient ? 'text-zinc-500' : statusColor} mt-1`}>{insufficient || !Number.isFinite(volRatio) ? 'N/A' : volRatio.toFixed(2)}</span>
        </div>
      </div>
    </div>

    <div className="border-t border-white/10 mt-4" />
  </div>
);

/*
Reg. Layout (Regular):
- Full widget presentation used for larger slots.
- Status row above a very large Standard Deviation value.
- Footer contains Avg P&L and Vol. Ratio with larger typography.
*/
const TradePlStdDevRegular: React.FC<LayoutProps> = ({ stdDev, avgPnL, volRatio, status, statusColor, insufficient }) => (
  <div className="h-full w-full flex flex-col p-4 justify-between">
    <div className="mb-10">
        <div className="flex justify-center mb-6">
            <span className={`inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-base font-semibold uppercase tracking-wider ${insufficient ? 'bg-zinc-500/10 text-zinc-500 dark:text-zinc-400 border border-zinc-500/40' : 'bg-red-500 text-zinc-900'}`}>
              {insufficient ? 'NEED 2+ TRADES' : status}
            </span>
        </div>

        <div className="text-6xl sm:text-6xl font-bold tracking-tight text-center">
          <span className={`${stdDev >= 0 && !insufficient ? 'text-emerald-600' : 'text-zinc-500'}`}>{insufficient ? 'N/A' : fmtMoneySmart(stdDev)}</span>
        </div>

    </div>

    <div className="border-t border-white/10 pt-3 flex items-center justify-between">
      <div className="flex flex-col">
        <span className="text-[14px] uppercase tracking-wider text-zinc-500 font-medium mb-0.5">AVG P&L</span>
        <span className={`text-xl font-bold ${insufficient && avgPnL === 0 ? 'text-zinc-500' : avgPnL >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{insufficient && avgPnL === 0 ? 'N/A' : fmtMoneySmart(avgPnL)}</span>
      </div>

      <div className="flex flex-col items-end">
        <span className="text-[14px] uppercase tracking-wider text-zinc-500 font-medium mb-0.5">VOL. RATIO</span>
        <span className={`text-xl font-bold ${insufficient ? 'text-zinc-500' : statusColor}`}>{insufficient || !Number.isFinite(volRatio) ? 'N/A' : volRatio.toFixed(2)}</span>
      </div>
    </div>
  </div>
);

export const TradePlStandardDeviationWidget: React.FC<TradePlStandardDeviationWidgetProps> = ({ dailyData, trades, size = 'mini' }) => {
  const { stdDev, avgPnL, volRatio, insufficient } = useMemo(() => {
    let pnlValues: number[] = [];

    if (Array.isArray(trades) && trades.length > 0) {
      pnlValues = trades
        .map((t) => resolveTradeRealizedPnl(t as unknown as AnyTrade))
        .filter((val): val is number => val !== null);
    } else if (Array.isArray(dailyData) && dailyData.length > 0) {
      const validDays = dailyData.filter((d) => d.trades === undefined || d.trades > 0);
      pnlValues = validDays.map((d) => d.pnl ?? 0);
    }

    const n = pnlValues.length;
    if (n === 0) return { stdDev: 0, avgPnL: 0, volRatio: 0, insufficient: true };

    const totalPnL = pnlValues.reduce((a, b) => a + b, 0);
    const mean = totalPnL / n;

    if (n < 2) return { stdDev: 0, avgPnL: mean, volRatio: 0, insufficient: true };

    // Sample variance (divide by N-1) when n > 1.
    const variance = pnlValues.reduce((a, b) => a + (b - mean) ** 2, 0) / (n - 1);
    const stdDev = Math.sqrt(variance);

    // Vol Ratio = StdDev / |Avg P&L|. If avg is 0, represent ratio as Infinity.
    const volRatio = mean === 0 ? Infinity : Math.abs(stdDev / mean);

    return { stdDev, avgPnL: mean, volRatio, insufficient: false };
  }, [trades, dailyData]);

  // Determine status based on Vol Ratio using thresholds:
  // Stable: ratio < 1.5
  // Volatile: 1.5 <= ratio <= 3.0
  // Erratic: ratio > 3.0
  let status = 'STABLE';
  let statusColor = 'text-emerald-600';
  let dotColor = 'bg-emerald-600';

  if (volRatio >= 1.5 && volRatio <= 3.0) {
    status = 'VOLATILE';
    statusColor = 'text-amber-400';
    dotColor = 'bg-amber-400';
  } else if (volRatio > 3.0) {
    status = 'ERRATIC';
    statusColor = 'text-red-500';
    dotColor = 'bg-red-500';
  }

  const isRegular = size === 'regular' || size === 'full';

  const layoutProps: LayoutProps = { stdDev, avgPnL, volRatio, status, statusColor, dotColor, insufficient };

  return isRegular ? <TradePlStdDevRegular {...layoutProps} /> : <TradePlStdDevMini {...layoutProps} />;
};
